import { useState, useCallback, useEffect } from 'react';
import { getActivityFeed, getErrorMessage, type ActivityFeedItem } from '@/lib/api';

interface UseActivityFeedReturn {
  activities: ActivityFeedItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching the global activity feed
 */
export const useActivityFeed = (limit: number = 5): UseActivityFeedReturn => {
  const [activities, setActivities] = useState<ActivityFeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActivities = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getActivityFeed(limit);
      setActivities(response.activities);
    } catch (err) {
      console.error('Failed to fetch activity feed:', err);
      setError(getErrorMessage(err));
      setActivities([]);
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return {
    activities,
    isLoading,
    error,
    refetch: fetchActivities,
  };
};

export default useActivityFeed;
